/**
 * Views over the expense ledger: date-range and month filters, a by-month summary, monthly recurring entries
 * and the file names used when receipts are bundled with the CSV. Pure; nothing is stored here.
 */
import type { Expense } from "./types";
import { expenseGross, isPledged } from "./budgetOps";

const round2 = (n: number) => Math.round(n * 100) / 100;
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "2026-10" for "2026-10-31"; "" when the date is not a YYYY-MM-DD date. */
export const monthKey = (date: string): string => (ISO.test(date) ? date.slice(0, 7) : "");

/** Months present in the entries, newest first. */
export function monthsIn(expenses: readonly Pick<Expense, "date">[]): string[] {
  return Array.from(new Set(expenses.map((e) => monthKey(e.date)).filter(Boolean))).sort().reverse();
}

/** Entries dated from..to inclusive. A blank bound is open. Entries with an unreadable date are kept only when no bound is set. */
export function filterByRange<T extends Pick<Expense, "date">>(expenses: readonly T[], from: string, to: string): T[] {
  const f = ISO.test(from) ? from : "", t = ISO.test(to) ? to : "";
  if (!f && !t) return [...expenses];
  return expenses.filter((e) => ISO.test(e.date) && (!f || e.date >= f) && (!t || e.date <= t));
}

/** First and last day of a "YYYY-MM" month, or null. */
export function monthRange(key: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return { from: `${key}-01`, to: `${key}-${String(last).padStart(2, "0")}` };
}

export interface MonthRow { month: string; cost: number; income: number }

/** Cost and income per month for entries in `currency`, oldest first. */
export function byMonth(expenses: readonly Expense[], currency: string): MonthRow[] {
  const map = new Map<string, MonthRow>();
  for (const e of expenses) {
    if (e.currency && e.currency !== currency) continue;
    const k = monthKey(e.date);
    if (!k || isPledged(e)) continue;
    const row = map.get(k) ?? { month: k, cost: 0, income: 0 };
    if (e.kind === "income") row.income = round2(row.income + expenseGross(e)); else row.cost = round2(row.cost + expenseGross(e));
    map.set(k, row);
  }
  return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
}

export const MAX_RECURRENCES = 60;

/**
 * Monthly dates from `start` up to and including `until`, on the start's day of the month. A day that does not
 * exist in a month (the 31st in April) moves to that month's last day. Empty when the dates are unusable or
 * `until` is before `start`; never more than MAX_RECURRENCES.
 */
export function monthlyDates(start: string, until: string): string[] {
  const s = ISO.exec(start), u = ISO.exec(until);
  if (!s || !u || until < start) return [];
  const y0 = Number(s[1]), m0 = Number(s[2]) - 1, day = Number(s[3]);
  const out: string[] = [];
  for (let i = 0; i < MAX_RECURRENCES; i++) {
    const y = y0 + Math.floor((m0 + i) / 12), m = (m0 + i) % 12;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const d = `${String(y).padStart(4, "0")}-${String(m + 1).padStart(2, "0")}-${String(Math.min(day, last)).padStart(2, "0")}`;
    if (d > until) break;
    out.push(d);
  }
  return out;
}

/** One copy of `base` per month between its date and `until`, each with a fresh id. Falls back to just `base`. */
export function expandMonthly(base: Expense, until: string, makeId: () => string): Expense[] {
  const dates = monthlyDates(base.date, until);
  if (!dates.length) return [base];
  return dates.map((date, i) => ({ ...base, id: i === 0 ? base.id : makeId(), date }));
}

/** A safe file name for a receipt inside the ZIP: keeps letters, digits, dot, dash and underscore. */
export function receiptZipName(date: string, originalPath: string, taken: Set<string>): string {
  const original = originalPath.split(/[\\/]/).pop() || "";
  const dot = original.lastIndexOf(".");
  const ext = dot > 0 ? original.slice(dot).replace(/[^\w.]/g, "").slice(0, 8) : "";
  const stem = (dot > 0 ? original.slice(0, dot) : original).replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "receipt";
  let name = `receipts/${ISO.test(date) ? date : "undated"}-${stem}${ext}`;
  for (let n = 2; taken.has(name); n++) name = `receipts/${ISO.test(date) ? date : "undated"}-${stem}-${n}${ext}`;
  taken.add(name);
  return name;
}
